import React, { type CSSProperties, type ReactNode } from 'react';

import {
	BACKGROUND_COLOR_CLASSES,
	type ColorName,
	COLORS,
} from 'constants/colors';
import { useDebounce } from 'hooks/useDebounce';
import { cn } from 'utils/classNames';
import { range } from 'utils/helpers';

const DURATION_CLASSES = {
	100: 'duration-100',
	200: 'duration-200',
	300: 'duration-300',
	400: 'duration-400',
	500: 'duration-500',
};

interface Props {
	borderColor?: ColorName;
	borderRadius?: number;
	/** Border width in pixels. */
	borderWidth?: number;
	children?: ReactNode;
	className?: string;
	contentBackgroundColor?: ColorName;
	contentClassName?: string;
	/** Fade out transition speed in milliseconds. */
	fadeOutTransitionSpeed?: keyof typeof DURATION_CLASSES;
	isActive?: boolean;
	length?: 'small' | 'medium' | 'large';
	/** Trail animation speed in milliseconds. */
	speed?: number;
	trailColor?: ColorName;
	trails?: 1 | 2;
	trailTrackClassName?: string;
}

/**
 * Container that has an animated trail in its border.
 *
 * For browsers that don't support the necessary CSS features, the border
 * animation is simply hidden. The border itself is still visible.
 */
export default function LoadingBorder({
	borderColor,
	borderRadius,
	borderWidth = 2,
	children,
	className,
	contentBackgroundColor = 'white',
	contentClassName,
	fadeOutTransitionSpeed = 200,
	isActive = false,
	length = 'medium',
	speed,
	trailColor = 'julaRed',
	trails = 2,
	trailTrackClassName,
}: Props) {
	const isActiveDebounced = useDebounce(
		isActive,
		isActive ? 1 : fadeOutTransitionSpeed,
	);

	const trailStyle: CSSProperties = {
		backgroundImage: `radial-gradient(100% 100% at right, ${COLORS[trailColor]}, transparent 50%)`,
	};
	const containerStyle: CSSProperties = { padding: `${borderWidth / 16}rem` };
	const contentStyle: CSSProperties = {};
	if (speed) {
		trailStyle.animationDuration = `${speed}ms`;
	}
	if (borderRadius) {
		containerStyle.borderRadius = `${borderRadius / 16}rem`;
		containerStyle.clipPath = `inset(0 0 0 0 round ${borderRadius / 16}rem)`;
		const innerRadius = Math.max(
			borderWidth > 2 ? 2 : 1,
			borderRadius - borderWidth,
		);
		contentStyle.borderRadius = `${innerRadius / 16}rem`;
	}

	return (
		<div className={cn('overflow-hidden', className)}>
			<div
				style={containerStyle}
				className={cn(
					'relative h-full',
					borderColor && BACKGROUND_COLOR_CLASSES[borderColor],
					trailTrackClassName,
				)}
			>
				{range(trails).map((i) => (
					// The trails are radial gradients that are anchored to the container
					// edge via offset-path. This is then animated with offset-distance.
					<div
						key={i}
						style={trailStyle}
						className={cn(
							// offset-path padding-box is the newest requirement (Safari 18,
							// Sep 2024), hide if not available.
							'supports-[not(offset-path:padding-box)]:hidden',
							'[offset-anchor:100%_50%] [offset-path:padding-box]',
							'bg-[radial-gradient(100%_100%_at_right,theme(colors.julaRed),transparent_50%)]',
							'absolute transition-opacity',
							DURATION_CLASSES[fadeOutTransitionSpeed],
							// Want a quick start, the prop only affects the fade out.
							isActive && 'duration-100',
							length === 'small' && 'h-10 w-20',
							length === 'medium' && 'h-20 w-40',
							length === 'large' && 'h-32 w-64',
							!isActive && 'opacity-0',
							// Keep playing the animations while fading out.
							i === 0 &&
								(isActive || isActiveDebounced) &&
								'animate-[offsetDistance100_1.5s_infinite_linear]',
							i === 1 &&
								(isActive || isActiveDebounced) &&
								'animate-[offsetDistance150_1.5s_infinite_linear] [offset-distance:50%]',
						)}
					/>
				))}
				<div
					style={contentStyle}
					className={cn(
						'relative z-1 h-full',
						BACKGROUND_COLOR_CLASSES[contentBackgroundColor],
						contentClassName,
					)}
				>
					{children}
				</div>
			</div>
		</div>
	);
}
LoadingBorder.displayName = 'LoadingBorder';
